import { useMemo } from "react";
import {
	keepPreviousData,
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import api from "../api/axiosInstance";
import { authAtom } from "../store/auth";

export const permissionKeys = {
	me: (userId) => ["permissions", "me", userId],
	options: ["permissions", "options"],
	roles: ["permissions", "roles"],
	role: (id) => ["permissions", "roles", id],
	endpoints: (filters) => ["permissions", "endpoints", filters],
};

export const useMyAccess = () => {
	const auth = useAtomValue(authAtom);
	const userId = auth?.user?.id ?? null;
	const query = useQuery({
		queryKey: permissionKeys.me(userId),
		enabled: Boolean(auth?.access_token),
		queryFn: async () => (await api.get("/permissions/me")).data,
		refetchInterval: 60 * 1000,
		refetchOnWindowFocus: true,
		retry: 1,
	});

	const access = useMemo(() => {
		if (!query.data) return null;
		return {
			...query.data,
			pageKeys: new Set(query.data.pages.map((page) => page.key)),
			scopeNames: new Set(query.data.scopes.map((scope) => scope.name)),
		};
	}, [query.data]);

	return { ...query, access };
};

export const usePermissionOptions = () =>
	useQuery({
		queryKey: permissionKeys.options,
		queryFn: async () => (await api.get("/permissions/options")).data,
	});

export const useRoleAccess = (roleId) =>
	useQuery({
		queryKey: permissionKeys.role(roleId),
		enabled: Boolean(roleId),
		queryFn: async () => (await api.get(`/permissions/roles/${roleId}`)).data,
	});

export const useEndpointPermissions = (filters) =>
	useQuery({
		queryKey: permissionKeys.endpoints(filters),
		queryFn: async () =>
			(await api.get("/permissions/endpoints", { params: filters })).data,
		placeholderData: keepPreviousData,
	});

const useApiMutation = (request) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (payload) => (await request(payload)).data,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["permissions"] });
			queryClient.invalidateQueries({ queryKey: ["roles"] });
			queryClient.invalidateQueries({ queryKey: ["scopes"] });
			queryClient.invalidateQueries({ queryKey: ["users"] });
		},
	});
};

export const usePermissionMutations = () => ({
	createRole: useApiMutation((body) => api.post("/permissions/roles", body)),
	updateRole: useApiMutation(({ id, ...body }) =>
		api.put(`/permissions/roles/${id}`, body)
	),
	deleteRole: useApiMutation((id) => api.delete(`/permissions/roles/${id}`)),
	setRoleAccess: useApiMutation(({ id, ...body }) =>
		api.put(`/permissions/roles/${id}/access`, body)
	),
	createScope: useApiMutation((body) => api.post("/permissions/scopes", body)),
	setEndpointRules: useApiMutation((body) =>
		api.put("/permissions/endpoints/rules", body)
	),
	bulkAssignEndpoints: useApiMutation((body) =>
		api.post("/permissions/endpoints/rules/bulk", body)
	),
});
